import { activeBrandId, type BrandId } from "./theme";
import geniusfeedTheme from "./themes/geniusfeed.json";
import pixupTheme from "./themes/pixup.json";
import sportsbookcoreTheme from "./themes/sportsbookcore.json";
import type { ThemeLanding } from "types/theme";
import {
  isThemePreviewMode,
  readThemePreviewConfig,
} from "utils/theme-preview-storage";

type AssetModuleContext = {
  keys(): string[];
  <T>(id: string): T;
};

const buildAssetManifest = (): Record<string, string> => {
  if (typeof require === "undefined") {
    return {};
  }

  let context: AssetModuleContext | undefined;
  try {
    // @ts-expect-error - webpack injects require.context for asset discovery
    context = require.context(
      "../assets",
      true,
      /\.(png|jpe?g|svg|gif|webp|avif)$/i
    );
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("[landing-config] Asset manifest build failed", error);
    }
    return {};
  }

  if (!context) {
    return {};
  }

  const assetContext = context;

  return assetContext.keys().reduce<Record<string, string>>((acc, key) => {
    const normalizedKey = key.replace(/^\.\//, "");
    acc[normalizedKey] = assetContext(key) as string;
    return acc;
  }, {});
};

const assetManifest = buildAssetManifest();

type LandingMetadata = {
  pageTitle: string;
  faviconHref?: string;
  heroPrimaryShapeText: string;
};

type LandingLogo = {
  src: string;
  alt?: string;
};

export type LandingAssets = {
  heroBackground: string;
  heroRightImage?: string;
  logos: {
    main: LandingLogo;
    simple: LandingLogo;
  };
  metadata: LandingMetadata;
};

type LandingConfig = {
  heroBackground: string;
  heroRightImage?: string;
  logos: {
    main: {
      src: string;
      alt?: string;
    };
    simple: {
      src: string;
      alt?: string;
    };
  };
  metadata: LandingMetadata;
};

const resolveAssetPath = (assetPath?: string): string => {
  if (!assetPath) return "";
  if (/^(https?:)?\/\//i.test(assetPath) || assetPath.startsWith("/")) {
    return assetPath;
  }

  const normalizedPath = assetPath.replace(/^\.\//, "");
  const manifestAsset = assetManifest[normalizedPath];
  if (manifestAsset) {
    return manifestAsset;
  }

  try {
    return new URL(`../assets/${normalizedPath}`, import.meta.url).href;
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn(
        `[landing-config] Asset could not be resolved: ${normalizedPath}`,
        error
      );
    }
    return assetPath;
  }
};

const coerceLanding = (config: LandingConfig): LandingAssets => {
  const normalizedMetadata: LandingMetadata = {
    ...config.metadata,
    faviconHref: config.metadata.faviconHref
      ? resolveAssetPath(config.metadata.faviconHref)
      : undefined,
  };

  return {
    heroBackground: resolveAssetPath(config.heroBackground),
    heroRightImage: config.heroRightImage
      ? resolveAssetPath(config.heroRightImage)
      : undefined,
    logos: {
      main: {
        src: resolveAssetPath(config.logos.main.src),
        alt: config.logos.main.alt,
      },
      simple: {
        src: resolveAssetPath(config.logos.simple.src),
        alt: config.logos.simple.alt,
      },
    },
    metadata: normalizedMetadata,
  };
};

type ThemeWithLanding = {
  landing?: LandingConfig;
};

const getLandingConfig = (
  theme: ThemeWithLanding,
  brand: BrandId
): LandingConfig => {
  if (!theme.landing) {
    throw new Error(`Landing configuration is missing for brand "${brand}"`);
  }
  return theme.landing;
};

const assetsByBrand: Record<BrandId, LandingAssets> = {
  geniusfeed: coerceLanding(
    getLandingConfig(geniusfeedTheme as ThemeWithLanding, "geniusfeed")
  ),
  pixup: coerceLanding(
    getLandingConfig(pixupTheme as ThemeWithLanding, "pixup")
  ),
  sportsbookcore: coerceLanding(
    getLandingConfig(sportsbookcoreTheme as ThemeWithLanding, "sportsbookcore")
  ),
};

const getLandingFallback = (brand?: string): LandingAssets => {
  if (brand && brand in assetsByBrand) {
    return assetsByBrand[brand as BrandId];
  }
  return assetsByBrand.geniusfeed;
};

const buildLandingAssetsFromTheme = (
  landing?: ThemeLanding,
  brand?: string
): LandingAssets | null => {
  if (!landing) return null;
  const fallback = getLandingFallback(brand);
  return {
    heroBackground: landing.heroBackground
      ? resolveAssetPath(landing.heroBackground)
      : fallback.heroBackground,
    heroRightImage: landing.heroRightImage
      ? resolveAssetPath(landing.heroRightImage)
      : fallback.heroRightImage,
    logos: {
      main: {
        src: landing.logos?.main?.src
          ? resolveAssetPath(landing.logos.main.src)
          : fallback.logos.main.src,
        alt: landing.logos?.main?.alt ?? fallback.logos.main.alt,
      },
      simple: {
        src: landing.logos?.simple?.src
          ? resolveAssetPath(landing.logos.simple.src)
          : fallback.logos.simple.src,
        alt: landing.logos?.simple?.alt ?? fallback.logos.simple.alt,
      },
    },
    metadata: {
      pageTitle: landing.metadata?.pageTitle ?? fallback.metadata.pageTitle,
      faviconHref: landing.metadata?.faviconHref
        ? resolveAssetPath(landing.metadata.faviconHref)
        : fallback.metadata.faviconHref,
      heroPrimaryShapeText:
        landing.metadata?.heroPrimaryShapeText ??
        fallback.metadata.heroPrimaryShapeText,
    },
  };
};

const previewPayload = isThemePreviewMode() ? readThemePreviewConfig() : null;
const previewLandingAssets = buildLandingAssetsFromTheme(
  previewPayload?.config?.landing,
  previewPayload?.customer
);

export const landingAssets =
  previewLandingAssets ?? assetsByBrand[activeBrandId];
